import * as React from 'react';
import * as _ from 'lodash';
import {
    DraggableModal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalTitle
} from '../../../../../common/modal';
import {
    CONDITION_TYPES,
    OptionData,
    ProjectLanguage,
    QuestionSummary
} from '@nexxt/common/types';

import { UilPlusCircle, UilMultiply } from '@iconscout/react-unicons';
import OptionConditionRule from './OptionConditionRule';
import {
    defaultOptionCondition,
    defaultRules
} from '../../../ConditionEditor/default';
import Menu from '../../../../../common/Menu';
import { getConfirmationBox } from '../../../../../common/swal/Sweetalert';
import ValidationService from '@nexxt/common/services/ValidationService';
import { INCA_VARIABLE_FUNCTION_TYPES } from '@nexxt/common/incaLogic/types';

interface ConditionModalProps {
    languages: ProjectLanguage[];
    option: OptionData;
    questionSummaries: QuestionSummary[];
    disabled: boolean;
    showError: boolean;
    conditionModalSetting: any;
    setConditionModalSetting: (setting) => void;
    onClose: () => void;
    onSave: (conditions, deleteConditionIDs) => void;
}

const ConditionModal: React.FC<ConditionModalProps> = ({
    languages,
    questionSummaries,
    option,
    disabled,
    conditionModalSetting,
    onClose,
    onSave,
    setConditionModalSetting
}: ConditionModalProps) => {
    const validationService = new ValidationService();
    const [conditions, setConditions] = React.useState(
        (option.conditions || [defaultOptionCondition()]) as any
    );
    const [deleteConditionIDs, setDeleteConditionIDs] = React.useState([]);
    const { isEdit } = conditionModalSetting;
    React.useEffect(() => {
        setConditions(
            option.conditions?.length
                ? option.conditions
                : [defaultOptionCondition()]
        );

        return () => {
            setConditions(null);
        };
    }, [option]);

    const addConditionRule = () => {
        const newRule = defaultRules();
        const newConditions = [...conditions];

        newConditions[newConditions.length - 1].rules.push(newRule);

        setConditions(newConditions);
        setConditionModalSetting({
            ...conditionModalSetting,
            editFlag: _.isEqual(option.conditions, newConditions)
        });
    };

    const handleQuestionSelect = (
        conditionIndex: number,
        ruleIndex: number,
        questionSummary
    ) => {
        const newCondition = [...conditions];
        const currentCondition = newCondition[conditionIndex];

        currentCondition.rules[ruleIndex] = {
            ...currentCondition.rules[ruleIndex],
            selectedReferenceQ: {
                id: questionSummary.id,
                label: questionSummary.label,
                value: questionSummary.value
            },
            compareOptions: []
        };
        newCondition[conditionIndex] = currentCondition;
        setConditions(newCondition);
        setConditionModalSetting({
            ...conditionModalSetting,
            editFlag: _.isEqual(option.conditions, newCondition)
        });
    };

    const handleOptionSelect = (
        conditionIndex: number,
        ruleIndex: number,
        type: 'options' | 'number' | 'string' | 'boolean',
        value,
        compareType: CONDITION_TYPES
    ) => {
        const newCondition = [...conditions];
        const currentCondition = newCondition[conditionIndex];

        switch (type) {
            case 'options':
                currentCondition.rules[ruleIndex] = {
                    ...currentCondition.rules[ruleIndex],
                    compareType: compareType,
                    compareOptions: value.map((opt) => ({
                        id: opt.id,
                        label: opt.label,
                        optionKey: opt.optionKey,
                        value: opt.value
                    }))
                };
                break;
            case 'number':
                currentCondition.rules[ruleIndex] = {
                    ...currentCondition.rules[ruleIndex],
                    compareType: CONDITION_TYPES.CUSTOM_VALUE,
                    compareNumber: value
                };
                break;
            case 'string':
                currentCondition.rules[ruleIndex] = {
                    ...currentCondition.rules[ruleIndex],
                    compareType: CONDITION_TYPES.CUSTOM_VALUE,
                    compareCustomValue: value
                };
                break;
            case 'boolean':
                currentCondition.rules[ruleIndex] = {
                    ...currentCondition.rules[ruleIndex],
                    compareType: compareType
                };
                break;
        }

        newCondition[conditionIndex] = currentCondition;
        setConditions(newCondition);
        setConditionModalSetting({
            ...conditionModalSetting,
            editFlag: _.isEqual(option.conditions, newCondition)
        });
    };

    const handleOperatorSelect = (
        conditionIndex: number,
        ruleIndex: number,
        selectedOperator
    ) => {
        const newCondition = [...conditions];
        const currentCondition = newCondition[conditionIndex];
        currentCondition.rules[ruleIndex] = {
            ...currentCondition.rules[ruleIndex],
            selectedOperator
        };
        if (
            [
                INCA_VARIABLE_FUNCTION_TYPES.IS_DISPLAYED,
                INCA_VARIABLE_FUNCTION_TYPES.IS_NOT_DISPLAYED
            ].includes(selectedOperator?.type)
        ) {
            currentCondition.rules[ruleIndex] = {
                ...currentCondition.rules[ruleIndex],
                compareType: CONDITION_TYPES.CUSTOM_VALUE
            };
        }
        newCondition[conditionIndex] = currentCondition;
        setConditions(newCondition);
        setConditionModalSetting({
            ...conditionModalSetting,
            editFlag: _.isEqual(option.conditions, newCondition)
        });
    };

    const removeConditionRule = async (conditionIndex, ruleIndex) => {
        const { isConfirmed } = await getConfirmationBox({
            message: ' This action cannot be undone.',
            showCloseButton: true,
            title: 'Delete Condition',
            messageAligned: 'left'
        });
        if (isConfirmed) {
            let newCondition = [...conditions];
            const currentCondition = newCondition[conditionIndex];
            const currentRuleId = currentCondition.rules?.[ruleIndex]?.ruleID;
            const currentConditionId = currentCondition.conditionID;
            currentCondition.rules = [
                ...currentCondition.rules.slice(0, ruleIndex),
                ...currentCondition.rules.slice(ruleIndex + 1)
            ];
            currentCondition.deletedRuleIDs = [
                ...(currentCondition.deletedRuleIDs || []),
                currentRuleId
            ];

            newCondition[conditionIndex] = currentCondition;

            if (!currentCondition.rules?.length) {
                newCondition = [
                    ...newCondition.slice(0, conditionIndex),
                    ...newCondition.slice(conditionIndex + 1)
                ];
            }
            setConditions(newCondition);
            currentConditionId &&
                setDeleteConditionIDs((deleteConditionIDs) => [
                    ...deleteConditionIDs,
                    currentConditionId
                ]);
            setConditionModalSetting({
                ...conditionModalSetting,
                editFlag: _.isEqual(option.conditions, newCondition)
            });
        }
    };

    const switchConditionRule = (conditionIndex, ruleIndex, type) => {
        let newConditions = [...conditions];
        const currentCondition = newConditions[conditionIndex];
        if (type.value === 'SWITCH_TO_CONDITION') {
            const deletedRuleIDs = [];
            const rules = [...currentCondition.rules.slice(ruleIndex)].map(
                (rule) => {
                    delete rule.conditionID;
                    rule?.ruleID && deletedRuleIDs.push(rule.ruleID);
                    return { ...rule, ruleID: 0, new: true };
                }
            );
            currentCondition.rules = [
                ...currentCondition.rules.slice(0, ruleIndex)
            ];
            currentCondition.deletedRuleIDs = [
                ...(currentCondition?.deletedRuleIDs || []),
                ...deletedRuleIDs
            ];
            const nextCondition = newConditions[conditionIndex + 1] || {
                conditionID: 0,
                isEdit: true,
                new: true,
                rules: []
            };

            nextCondition.rules.push(...rules);
            newConditions[conditionIndex + 1] = nextCondition;
        } else {
            const currentRules = currentCondition.rules.slice().map((rule) => {
                delete rule?.conditionID;
                return { ...rule, ruleID: 0, new: true };
            });
            currentCondition?.conditionID &&
                setDeleteConditionIDs((deleteIds) => [
                    ...deleteIds,
                    currentCondition.conditionID
                ]);
            const prevCondition = newConditions[conditionIndex - 1];

            prevCondition.rules = [...prevCondition.rules, ...currentRules];

            newConditions[conditionIndex - 1] = prevCondition;
            newConditions = [
                ...newConditions.slice(0, conditionIndex),
                ...newConditions.slice(conditionIndex + 1)
            ];
        }
        setConditions(newConditions);
        setConditionModalSetting({
            ...conditionModalSetting,
            editFlag: _.isEqual(option.conditions, newConditions)
        });
    };

    const deleteCondition = async () => {
        const { isConfirmed } = await getConfirmationBox({
            message: ' This action cannot be undone.',
            showCloseButton: true,
            title: 'Delete Condition',
            messageAligned: 'left'
        });

        if (isConfirmed) {
            const conditionIds = conditions.map(
                (condition) => condition.conditionID
            );
            onClose();
            onSave(null, conditionIds);
        }
    };

    const validate = validationService.validateAllConditions(
        CONDITION_TYPES.PRECONDITION,
        conditions as any
    );
    const renderCondition = () => {
        const render = conditions?.map((condition, conditionIndex) => {
            const c = condition.rules.map((rule, ruleIndex) => {
                return (
                    <>
                        {!(ruleIndex === 0 && conditionIndex === 0) && (
                            <>
                                {isEdit && (
                                    <div className="condition--rule--separator">
                                        <span
                                            className="condition--rule--remove"
                                            onClick={() =>
                                                removeConditionRule(
                                                    conditionIndex,
                                                    ruleIndex
                                                )
                                            }
                                        >
                                            <UilMultiply size={20} />
                                        </span>
                                    </div>
                                )}
                                {isEdit ? (
                                    <div className="condition--rule--rel is-edit">
                                        <Menu
                                            items={[
                                                {
                                                    label: 'and',
                                                    value: 'SWITCH_TO_RULE'
                                                },
                                                {
                                                    label: 'or',
                                                    value: 'SWITCH_TO_CONDITION'
                                                }
                                            ]}
                                            selectedItem={
                                                ruleIndex
                                                    ? 'SWITCH_TO_RULE'
                                                    : 'SWITCH_TO_CONDITION'
                                            }
                                            onClickItem={(value) =>
                                                switchConditionRule(
                                                    conditionIndex,
                                                    ruleIndex,
                                                    value
                                                )
                                            }
                                            disabled={!isEdit}
                                        />
                                    </div>
                                ) : (
                                    <div className="condition--rule--rel">
                                        {ruleIndex ? 'AND' : 'OR'}
                                    </div>
                                )}
                            </>
                        )}
                        <OptionConditionRule
                            key={`rule-${option.value}-${conditionIndex}-${ruleIndex}`}
                            isEdit={isEdit}
                            rule={rule}
                            questionSummaries={questionSummaries}
                            handleQuestionSelect={(questionSummary) =>
                                handleQuestionSelect(
                                    conditionIndex,
                                    ruleIndex,
                                    questionSummary
                                )
                            }
                            handleOptionSelect={(type, option) =>
                                handleOptionSelect(
                                    conditionIndex,
                                    ruleIndex,
                                    type,
                                    option,
                                    rule.compareType
                                )
                            }
                            handleOperatorSelect={(operator) =>
                                handleOperatorSelect(
                                    conditionIndex,
                                    ruleIndex,
                                    operator
                                )
                            }
                            option={option}
                            showError={!!rule?.ruleID}
                            errorObject={
                                validate?.[conditionIndex]?.[ruleIndex]
                            }
                        />
                    </>
                );
            });
            return c;
        });

        return (
            <>
                {render}
                {isEdit && (
                    <div
                        className="condition--rule--row d-flex justify-content-center add-button"
                        onClick={addConditionRule}
                    >
                        <UilPlusCircle size={20} /> Add Another Rule
                    </div>
                )}
            </>
        );
    };
    return (
        <DraggableModal
            className="modal--condition modal__heightauto"
            isOpen={true}
        >
            <div>
                <ModalHeader onClose={onClose}>
                    <ModalTitle>
                        Only show{' '}
                        <span className="hashtag">
                            #{option.label || `option ${option.value}`}
                        </span>{' '}
                        if:
                    </ModalTitle>
                </ModalHeader>
                <ModalBody>{renderCondition()}</ModalBody>
                <ModalFooter>
                    <button
                        className="btn btn--xs btn--transparent m-r-10"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    {!isEdit && !disabled && (
                        <button
                            className="btn btn--xs btn--transparent m-r-10"
                            onClick={deleteCondition}
                        >
                            Delete conditions
                        </button>
                    )}
                    {!disabled && (
                        <button
                            className="btn btn--sm btn--green"
                            disabled={!!validate.isError}
                            onClick={() => {
                                if (!isEdit) {
                                    setConditionModalSetting({
                                        ...conditionModalSetting,
                                        isEdit: true
                                    });
                                    return;
                                }
                                onSave(conditions, deleteConditionIDs);
                            }}
                        >
                            {isEdit ? 'Apply' : 'Edit'}
                        </button>
                    )}
                </ModalFooter>
            </div>
        </DraggableModal>
    );
};

export default ConditionModal;
